import {
  type ConsentsPageQuerySchema,
  type CreateLegalDocumentRequest,
  type CreateLegalVersionRequest,
  type LegalDocument,
  type LegalDocumentDetail,
  type LegalDocumentsListQuerySchema,
  type LegalDocumentVersion,
  type LegalDocumentVersionDetail,
  type Paginated,
  type PublishLegalVersionRequest,
  type UpdateLegalDocumentRequest,
  type UpdateLegalVersionRequest,
  type UserConsent,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

type DocumentsParams = z.input<typeof LegalDocumentsListQuerySchema>;
type ConsentsParams = z.input<typeof ConsentsPageQuerySchema>;

export const legalApi = createApi({
  reducerPath: 'legalApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Documents', 'Document', 'Versions', 'Version', 'Consents'],
  endpoints: (builder) => ({
    listDocuments: builder.query<Paginated<LegalDocument>, DocumentsParams>({
      query: (params) => ({ url: '/admin/legal/documents', method: 'GET', params }),
      providesTags: ['Documents'],
    }),

    document: builder.query<LegalDocumentDetail, string>({
      query: (id) => ({ url: `/admin/legal/documents/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Document' as const, id }],
    }),

    createDocument: builder.mutation<LegalDocumentDetail, CreateLegalDocumentRequest>({
      query: (body) => ({ url: '/admin/legal/documents', method: 'POST', body }),
      invalidatesTags: ['Documents'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceDocumentChange(queryFulfilled.then(({ data }) => data.id));
      },
    }),

    updateDocument: builder.mutation<
      LegalDocumentDetail,
      { id: string; body: UpdateLegalDocumentRequest }
    >({
      query: ({ id, body }) => ({ url: `/admin/legal/documents/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Document' as const, id },
        'Documents' as const,
      ],
      async onQueryStarted({ id }, { queryFulfilled }) {
        await announceDocumentChange(queryFulfilled.then(() => id));
      },
    }),

    deleteDocument: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/legal/documents/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Document' as const, id },
        'Documents' as const,
        { type: 'Versions' as const, id },
      ],
      async onQueryStarted(_id, { queryFulfilled }) {
        await announceDocumentChange(queryFulfilled.then(() => null));
      },
    }),

    reorderDocuments: builder.mutation<void, readonly string[]>({
      query: (ids) => ({ url: '/admin/legal/documents/order', method: 'PUT', body: { ids } }),
      invalidatesTags: ['Documents'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceDocumentChange(queryFulfilled.then(() => null));
      },
    }),

    /** Newest first, and the whole history — a document has a handful of versions, never a page. */
    documentVersions: builder.query<LegalDocumentVersion[], string>({
      query: (documentId) => ({
        url: `/admin/legal/documents/${documentId}/versions`,
        method: 'GET',
      }),
      providesTags: (_result, _error, documentId) => [
        { type: 'Versions' as const, id: documentId },
      ],
    }),

    createDraft: builder.mutation<
      LegalDocumentVersionDetail,
      { documentId: string; body: CreateLegalVersionRequest }
    >({
      query: ({ documentId, body }) => ({
        url: `/admin/legal/documents/${documentId}/versions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { documentId }) => [
        { type: 'Versions' as const, id: documentId },
        { type: 'Document' as const, id: documentId },
        'Documents' as const,
      ],
      async onQueryStarted({ documentId }, { queryFulfilled }) {
        await announceVersionChange(
          documentId,
          queryFulfilled.then(({ data }) => data.id),
        );
      },
    }),

    version: builder.query<LegalDocumentVersionDetail, string>({
      query: (id) => ({ url: `/admin/legal/versions/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Version' as const, id }],
    }),

    updateDraft: builder.mutation<
      LegalDocumentVersionDetail,
      { id: string; documentId: string; body: UpdateLegalVersionRequest }
    >({
      query: ({ id, body }) => ({ url: `/admin/legal/versions/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id, documentId }) => [
        { type: 'Version' as const, id },
        { type: 'Versions' as const, id: documentId },
      ],
      async onQueryStarted({ id, documentId }, { queryFulfilled }) {
        await announceVersionChange(
          documentId,
          queryFulfilled.then(() => id),
        );
      },
    }),

    deleteDraft: builder.mutation<void, { id: string; documentId: string }>({
      query: ({ id }) => ({ url: `/admin/legal/versions/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id, documentId }) => [
        { type: 'Version' as const, id },
        { type: 'Versions' as const, id: documentId },
        { type: 'Document' as const, id: documentId },
        'Documents' as const,
      ],
      async onQueryStarted({ documentId }, { queryFulfilled }) {
        await announceVersionChange(
          documentId,
          queryFulfilled.then(() => null),
        );
      },
    }),

    /**
     * The irreversible act. It moves the document's current version too, so
     * the document and the list are invalidated with the history.
     */
    publishVersion: builder.mutation<
      LegalDocumentVersionDetail,
      { id: string; documentId: string; body: PublishLegalVersionRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/legal/versions/${id}/publish`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id, documentId }) => [
        { type: 'Version' as const, id },
        { type: 'Versions' as const, id: documentId },
        { type: 'Document' as const, id: documentId },
        'Documents' as const,
      ],
      async onQueryStarted({ id, documentId }, { queryFulfilled }) {
        await announceVersionChange(
          documentId,
          queryFulfilled.then(() => id),
        );
      },
    }),

    versionConsents: builder.query<
      Paginated<UserConsent>,
      { versionId: string; params: ConsentsParams }
    >({
      query: ({ versionId, params }) => ({
        url: `/admin/legal/versions/${versionId}/consents`,
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { versionId }) => [
        { type: 'Consents' as const, id: versionId },
      ],
    }),
  }),
});

export const {
  useListDocumentsQuery,
  useDocumentQuery,
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useReorderDocumentsMutation,
  useDocumentVersionsQuery,
  useCreateDraftMutation,
  useVersionQuery,
  useUpdateDraftMutation,
  useDeleteDraftMutation,
  usePublishVersionMutation,
  useVersionConsentsQuery,
} = legalApi;

// Published only once the write has landed; a refused write announces nothing.
async function announceDocumentChange(landed: Promise<string | null>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'legal.document.changed', documentId: await landed });
  } catch {
    // Refused. The mutation's own error surfaces it.
  }
}

async function announceVersionChange(
  documentId: string,
  landed: Promise<string | null>,
): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'legal.version.changed', documentId, versionId: await landed });
  } catch {
    // Refused. The mutation's own error surfaces it.
  }
}
